import { search, INDEX_NAME } from "algolia-instant-search";

if (!customElements.get("fyre-collection-families-filter")) {
  class FyreCollectionFamiliesFilter extends HTMLElement {
    connectedCallback() {
      this.track = this.querySelector('.fyre-cf__cards');
      this.progress = this.querySelector('.fyre-cf__progress');
      this.bar = this.querySelector('.fyre-cf__progress-bar');

      if (!this.track) return;

      this.isDown = false;
      this.startX = 0;
      this.startScroll = 0;
      this.didDrag = false;

      this.init();
      window.addEventListener("resize", () => this.init());
      this.track.addEventListener("scroll", () => this.updateBar(), {
        passive: true,
      });
      this.track.addEventListener("mousedown", (e) => this.onMouseDown(e));
      this.track.addEventListener("mousemove", (e) => this.onMouseMove(e));
      this.track.addEventListener("mouseup", () => this.endDrag());
      this.track.addEventListener("mouseleave", () => this.endDrag());
      document.addEventListener("mouseup", () => this.endDrag());
      this.track.addEventListener("click", (e) => this.onFilterClick(e));

      // family lives in InstantSearch's managed uiState (round-trips
      // through the router — see algolia-instant-search.js and
      // main-collection.js's familyCollectionWidget), so the highlight is
      // derived from search state rather than parsed from the URL. That keeps
      // Back/Forward and other deep links in sync, not just the initial load.
      this.syncActiveCardsFromState();
      search.on("render", () => this.syncActiveCardsFromState());
    }

    syncActiveCardsFromState() {
      const handles = search.getUiState()[INDEX_NAME]?.family?.handles ?? [];
      const cards = Array.from(this.track.querySelectorAll("[data-cf-filter]"));
      cards.forEach((card) => {
        card.classList.toggle(
          "fyre-cf__card--active",
          handles.includes(card.dataset.colHandle),
        );
      });
    }

    init() {
      this.checkScrollable();
      this.updateBar();
    }

    checkScrollable() {
      const canScroll = this.track.scrollWidth > this.track.clientWidth + 1;
      this.track.classList.toggle("no-scroll", !canScroll);
      if (this.progress) this.progress.style.display = canScroll ? "" : "none";
    }

    updateBar() {
      if (!this.bar || !this.progress) return;
      const max = this.track.scrollWidth - this.track.clientWidth;
      const ratio = max > 0 ? Math.min(this.track.scrollLeft / max, 1) : 0;
      const barWidth =
        (this.track.clientWidth / this.track.scrollWidth) *
        this.progress.clientWidth;
      const maxLeft = this.progress.clientWidth - barWidth;
      this.bar.style.width = `${barWidth}px`;
      this.bar.style.left = `${ratio * maxLeft}px`;
    }

    onMouseDown(e) {
      if (this.track.classList.contains("no-scroll")) return;
      this.isDown = true;
      this.didDrag = false;
      this.track.classList.add("is-dragging");
      this.startX = e.pageX - this.track.getBoundingClientRect().left;
      this.startScroll = this.track.scrollLeft;
    }

    onMouseMove(e) {
      if (!this.isDown) return;
      e.preventDefault();
      const x = e.pageX - this.track.getBoundingClientRect().left;
      const delta = x - this.startX;
      if (Math.abs(delta) > 4) this.didDrag = true;
      this.track.scrollLeft = this.startScroll - delta * 1.5;
    }

    endDrag() {
      this.isDown = false;
      this.track.classList.remove("is-dragging");
    }

    onFilterClick(e) {
      if (this.didDrag) return;
      const btn = e.target.closest("[data-cf-filter]");
      if (!btn) return;

      btn.classList.toggle("fyre-cf__card--active");
      this.pushActiveFamily();
    }

    // Routes through InstantSearch's own state instead of poking
    // search.helper directly — poking the helper makes the vehicle fitment
    // widget's clause fragile (last writer wins on the shared `filters`
    // string) and never touches the URL. setUiState is a genuine state change,
    // so it composes with familyCollectionWidget/fitmentVehicleWidget and
    // pushes exactly one history entry per click (Back correctly undoes it).
    pushActiveFamily() {
      const active = Array.from(
        this.track.querySelectorAll("[data-cf-filter].fyre-cf__card--active"),
      );
      const handles = active.map((el) => el.dataset.colHandle).filter(Boolean);
      search.setUiState((uiState) => ({
        ...uiState,
        [INDEX_NAME]: {
          ...uiState[INDEX_NAME],
          family: { handles },
        },
      }));
    }
  }

  customElements.define(
    "fyre-collection-families-filter",
    FyreCollectionFamiliesFilter,
  );
}
